import {combine, guard, merge, sample} from 'effector';

import i18next from 'i18next';
import {toast} from 'react-toastify';

import {RestError, showRestError} from 'core/errors';
import {history} from 'core/history';
import {initFiltersTable, initSortingsTable} from 'core/hooks/grid2.0';
import {ROUTE} from 'core/routes';
import {mapFilters, mapSortings} from 'utils/grid-mappers';

import {
    createGdsFx,
    data$,
    defaultFilters,
    defaultSortings,
    fetchGdsDetailFx,
    fetchGdsGridFx,
    filters$,
    filtersChanged,
    filtersReset,
    Gate,
    gdsCodeList$,
    customsNameAndNumber$,
    gdsDetail$,
    gdsDetailGate,
    gdsIdChanged,
    gdsStorageLocation$,
    pageNumber$,
    pageSize$,
    search$,
    searchChanged,
    sortings$,
    sortingsChanged,
    sortingsReset,
    stateReset,
    tableStateChanged,
    updateGdsCodeList,
    updateCustomsNameAndNumber,
    updateGdsStorageLocation,
    saveGdsChangesClicked,
    saveGdsChangesFx,
    gdsId$,
    errorTabs$,
    handleErrorTabs,
    submitGdsFx,
} from './index';

data$.on(fetchGdsGridFx.done, (state, {result}) => result).reset(Gate.close);
pageNumber$.reset(filtersChanged, sortingsChanged, stateReset, searchChanged);
pageSize$.reset(stateReset);

initFiltersTable(filters$, filtersChanged, filtersReset, defaultFilters, stateReset);
initSortingsTable(sortings$, sortingsChanged, sortingsReset, defaultSortings, stateReset);

errorTabs$
    .on(handleErrorTabs, (state, value) => value)
    .reset(gdsDetailGate.close, saveGdsChangesFx.doneData);

sample({
    clock: merge([Gate.open, tableStateChanged, searchChanged]),
    source: combine({
        sortings: sortings$,
        pageNumber: pageNumber$,
        pageSize: pageSize$,
        filters: filters$,
        search: search$,
    }),
    fn: ({pageNumber, pageSize, filters, sortings, search}) => ({
        paging: {
            pageNumber: Number(pageNumber) + 1,
            pageSize,
        },
        filters: mapFilters(filters),
        sortings: mapSortings(sortings),
        searchField: search,
    }),
    target: fetchGdsGridFx,
});

fetchGdsGridFx.fail.watch(({error}) => showRestError(error as RestError));

sample({
    clock: gdsDetailGate.open,
    fn: ({id}: {id: string}) => id,
    target: gdsIdChanged,
});

const filteredGateOpen = guard({
    clock: gdsDetailGate.open,
    filter: ({id}: {id: string}) => !!id,
});

sample({
    clock: filteredGateOpen,
    fn: ({id}: {id: string}) => id,
    target: fetchGdsDetailFx,
});

gdsDetail$.on(fetchGdsDetailFx.done, (state, {result}) => result).reset(gdsDetailGate.close);

customsNameAndNumber$
    .on(updateCustomsNameAndNumber, (state, data) => data)
    .reset(gdsDetailGate.close);
gdsStorageLocation$.on(updateGdsStorageLocation, (state, data) => data).reset(gdsDetailGate.close);
gdsCodeList$
    .on(updateGdsCodeList, (state, {data, action = 'add', index}) => {
        if (action === 'add') {
            if (!data) {
                return state;
            }
            return [...state, data];
        }
        return state.filter((item, i) => i !== index);
    })
    .reset(gdsDetailGate.close);

const hasInfoChanges$ = combine({
    old: gdsDetail$,
    new: combine({
        customsNameAndNumber: customsNameAndNumber$,
        gdsStorageLocation: gdsStorageLocation$,
    }),
}).map(({old, new: newData}) => {
    if (!old) return false;

    const oldCustomsId = old.customsId;
    const newCustomsId = Number(newData.customsNameAndNumber.number);

    const oldStorageLocation = old.storageLocation;
    const newStorageLocation = newData.gdsStorageLocation.value;

    return oldCustomsId !== newCustomsId || oldStorageLocation !== newStorageLocation;
});

const hasCodeListChanges$ = combine({
    old: gdsDetail$,
    new: gdsCodeList$,
}).map(({old, new: newCodeList}) => {
    if (!old) return false;

    const oldCodes = old.customsMarkingCodeList.map((item) => item.code).sort();
    const newCodes = newCodeList.map((item) => item.code).sort();

    if (oldCodes.length !== newCodes.length) return true;

    return oldCodes.some((code, index) => code !== newCodes[index]);
});

export const saveData$ = combine({
    gdsId: gdsId$,
    hasInfoChanges: hasInfoChanges$,
    hasCodeListChanges: hasCodeListChanges$,
    customsNameAndNumber: customsNameAndNumber$,
    gdsStorageLocation: gdsStorageLocation$,
    oldGdsDetail: gdsDetail$,
    newGdsCodeList: gdsCodeList$,
});

const filteredSaveData = guard({
    source: saveData$,
    clock: saveGdsChangesClicked,
    filter: ({gdsId}) => !!gdsId,
});

sample({
    clock: filteredSaveData,
    fn: ({
        gdsId,
        hasInfoChanges,
        hasCodeListChanges,
        customsNameAndNumber,
        gdsStorageLocation,
        oldGdsDetail,
        newGdsCodeList,
    }) => {
        const infoParams = hasInfoChanges
            ? {
                  customsId: Number(customsNameAndNumber.value),
                  storageLocation: gdsStorageLocation.value,
              }
            : undefined;

        let codeListParams: {[key: string]: boolean} | undefined;
        if (hasCodeListChanges && oldGdsDetail) {
            codeListParams = {};
            const oldCodes = new Set(oldGdsDetail.customsMarkingCodeList.map((item) => item.code));
            const newCodes = new Set(newGdsCodeList.map((item) => item.code));

            oldCodes.forEach((code) => {
                if (!newCodes.has(code)) {
                    codeListParams![code] = false;
                }
            });

            newCodes.forEach((code) => {
                if (!oldCodes.has(code)) {
                    codeListParams![code] = true;
                }
            });
        }

        return {
            gdsId: gdsId!,
            hasInfoChanges,
            hasCodeListChanges,
            infoParams,
            codeListParams,
        };
    },
    target: saveGdsChangesFx,
});

saveGdsChangesFx.doneData.watch((result) => {
    if (result.success && result.message !== 'No changes') {
        toast.success(i18next.t('pages.documents.gds.messages.saved'));
        const gdsId = gdsId$.getState();
        if (gdsId) {
            fetchGdsDetailFx(gdsId);
        }
    }
});

saveGdsChangesFx.fail.watch(({error}) => {
    const err = error as any;
    if (err.errorTabs && Array.isArray(err.errorTabs)) {
        handleErrorTabs(err.errorTabs);
    }

    if (err.rejectedReasons && Array.isArray(err.rejectedReasons)) {
        err.rejectedReasons.forEach((reason: any) => {
            showRestError(reason as RestError);
        });
    } else {
        showRestError(error as RestError);
    }
});

fetchGdsDetailFx.fail.watch(({error}) => showRestError(error as RestError));

createGdsFx.doneData.watch((data) => {
    history.push(ROUTE.DOCUMENTS_GDS);
    toast.success(i18next.t('pages.documents.gds.messages.created', {number: data}));
});
createGdsFx.fail.watch(({error}) => showRestError(error as RestError));

submitGdsFx.doneData.watch(() => {
    history.push(ROUTE.DOCUMENTS_GDS);
    toast.success(i18next.t('pages.documents.gds.messages.submitted'));
});
submitGdsFx.fail.watch(({error}) => showRestError(error as RestError));
